import {
  ISmartCanvasClickedItemEvent,
  ISmartCanvasLayer,
  ISmartCanvasOptions,
  ISmartCanvasPolygon,
  ISmartCanvasSelectionArea,
  ISmartCanvasWordOptions,
} from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { IPageDimensions } from 'src/modules/shared/models/interfaces/page-dimensions.interface';
import { SmartCanvasWordOptions } from 'src/modules/shared/models/smart-canvas.model';
import { Calcs } from 'src/modules/shared/utilities/calculations';
import { Point2D, Polygon } from 'src/modules/shared/models/robots.model';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';
import { IFileLine, IFilePageAndWords, IFileWord } from 'src/modules/shared/models/interfaces/files.interface';
import { GroupConfiguration } from 'src/modules/shared/models/group-keys.model';
import { WordDataTypeEnum } from 'src/modules/shared/models/enums/word-data-type.enum';

declare const Konva: any;

@Component({
  selector: 'hcr-smart-canvas-words',
  template: '',
})
export class SmartCanvasWordsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() layer: ISmartCanvasLayer;
  @Input() selectedFilePagesAndWords: IFilePageAndWords[];
  @Input() smartCanvasOptions: ISmartCanvasOptions;
  @Input() stagePageSizes: IPageDimensions[];
  @Input() konvaStage: any; //Konva.Stage;
  @Input() selectedGroupConfiguration: GroupConfiguration;
  @Input() states: { [key: string]: boolean };
  @Input() pagesToShow: number[];

  public selectedItems: IFileWord[] | IFileLine[] = [];
  public activeSelectionArea: ISmartCanvasSelectionArea;

  private _$ready = new BehaviorSubject<boolean>(false);
  private _subscriptions: { [key: string]: Subscription } = {};
  private _linesGroup: ISmartCanvasLayer = { konvaLayer: new Konva.Group(), polygons: [] };
  private _wordsGroup: ISmartCanvasLayer = { konvaLayer: new Konva.Group(), polygons: [] };
  private _defaultWordOptions: ISmartCanvasWordOptions = new SmartCanvasWordOptions({ showTooltip: true, clickable: true });
  private _groupKeyPopupTimeout: Subscription;

  constructor(private _eventEmitterService: EventEmitterService) {}

  private _hasSpecialType(item: IFileWord | IFileLine): boolean {
    return item.processedValues && item.processedValues.length > 0;
  }

  private _drawWordsFromFileData(): void {
    this._linesGroup.konvaLayer.destroyChildren();
    this._wordsGroup.konvaLayer.destroyChildren();
    this.layer.polygons = [];

    this.pagesToShow.forEach((pageNumber, pageIndex) => {
      const page = this.selectedFilePagesAndWords[pageIndex];
      if (page) {
        page.lines.forEach((line) => {
          const poly = this._drawWordOrLine(line, pageNumber, pageIndex);
          this._linesGroup.konvaLayer.add(poly);
          this.layer.polygons.push({ poly, content: line, pageIndex, pageNumber });
          this._linesGroup.polygons.push({ poly, content: line, pageIndex, pageNumber });

          line.words.forEach((word) => {
            const poly = this._drawWordOrLine(word, pageNumber, pageIndex);
            this._wordsGroup.konvaLayer.add(poly);
            this.layer.polygons.push({ poly, content: word, pageIndex, pageNumber });
            this._wordsGroup.polygons.push({ poly, content: word, pageIndex, pageNumber });
          });
        });
      }
    });
  }

  private _setFillAndOpacityToPoly(poly: any) {
    const options = { ...this._defaultWordOptions, ...this.layer.options };

    if (!this.smartCanvasOptions.showWordHighlights) {
      options.fill = 'transparent';
      options.opacity = 0;
    } else if (this._hasSpecialType(poly.attrs.word) && this.smartCanvasOptions.customStyles.enableProcessedWordHighlight) {
      options.fill = this.smartCanvasOptions.customStyles.words.processedWords.fill;
      options.opacity = this.smartCanvasOptions.customStyles.words.processedWords.opacity;
    } else {
      options.fill = this.smartCanvasOptions.customStyles.words.defaultWords.fill;
      options.opacity = this.smartCanvasOptions.customStyles.words.defaultWords.opacity;
    }

    poly.originalFill = options.fill;
    poly.fill(options.fill);
    poly.opacity(options.opacity);
  }

  private _drawWordOrLine(item: IFileWord | IFileLine, pageNumber: number, pageIndex: number): any {
    const options = { ...this._defaultWordOptions, ...this.layer.options };

    const points = Calcs.boundingBoxInchToPixelsRelative(
      item.boundingBoxRelative,
      {
        width: this.selectedFilePagesAndWords[pageIndex].width,
        height: this.selectedFilePagesAndWords[pageIndex].height,
        orientation: this.selectedFilePagesAndWords[pageIndex].angle,
        unit: this.selectedFilePagesAndWords[pageIndex].unit,
      },
      this.stagePageSizes[pageIndex].width
    );

    const poly = new Konva.Line({
      points: points,
      closed: true,
      lineJoin: 'round',
      lineCap: 'round',
      word: item,
      x: this.stagePageSizes[pageIndex].width * pageIndex + this.smartCanvasOptions.pageGutter * pageIndex,
      y: 0,
    });

    this._setFillAndOpacityToPoly(poly);

    if (options.showTooltip) {
      poly.on('mouseover.canvas-words', (opt) => {
        this._onHover(opt, options, pageIndex, pageNumber, item, poly);
      });

      poly.on('mouseout.canvas-words', () => {
        this._onMouseOut();
      });
    } else {
      poly.listening(false);
    }

    return poly;
  }

  private _showSplittedWords(show?: boolean): void {
    if (typeof show !== 'undefined') {
      this.states.splitWords = show;
    }

    if (this.states.splitWords) {
      this._linesGroup.konvaLayer.hide();
      this._wordsGroup.konvaLayer.show();
    } else {
      this._linesGroup.konvaLayer.show();
      this._wordsGroup.konvaLayer.hide();
    }
  }

  private _onHover(
    opt: any,
    options: ISmartCanvasWordOptions,
    pageIndex: number,
    pageNumber: number,
    item: IFileWord | IFileLine,
    poly: any
  ): void {
    if (options.clickable) {
      this.konvaStage.container().style.cursor = 'pointer';

      if (this._groupKeyPopupTimeout) {
        this._groupKeyPopupTimeout.unsubscribe();
      }

      if (this.smartCanvasOptions.showGroupKeysOnHover && this.smartCanvasOptions.showGroupKeys) {
        this.selectedItems = [opt.currentTarget.attrs.word];

        const polygon = new Polygon(
          Calcs.boundingBoxPixelsToRelative(<number[]>opt.currentTarget.attrs.points, this.stagePageSizes[pageIndex])
        );

        this.activeSelectionArea = {
          boundingBox: polygon.boundingBox,
          centroid: polygon.centroid,
          pageNumber: pageNumber,
          pageIndex: pageIndex,
          polygonsInside: Calcs.getPolygonsFromCanvasArea(
            <number[]>opt.currentTarget.attrs.points,
            pageIndex,
            this.states.splitWords ? this._wordsGroup : this._linesGroup
          ),
          fixedTooltipPosition: false,
        };

        this._eventEmitterService.get<ISmartCanvasSelectionArea>('show-group-key-selection').emit(this.activeSelectionArea);
      } else {
        this._eventEmitterService.get<ISmartCanvasPolygon>('show-canvas-tooltip').emit({ content: item, pageIndex, pageNumber, poly });
      }
    }
  }

  private _onMouseOut(): void {
    this._eventEmitterService.get('hide-canvas-tooltip').emit();

    this._groupKeyPopupTimeout = timer(50).subscribe(() => {
      this._eventEmitterService.get('hide-group-key-selection').emit();
      this._groupKeyPopupTimeout.unsubscribe();
      delete this._groupKeyPopupTimeout;
    });

    this.konvaStage.container().style.cursor = 'default';
  }

  private _onClick(opt: any): void {
    const options = { ...this._defaultWordOptions, ...this.layer.options };

    // Se é permitido clicar em palavras nesta layer, a seleção de palavras está habilitada e o clique foi em uma palavra/frase
    if (options.clickable && this.smartCanvasOptions.enableWordSelection && opt.target.attrs.word) {
      this.selectedItems = [opt.target.attrs.word];
      const pageNumber = this.selectedItems[0].pageNumber;
      const pageIndex = this.selectedItems[0].pageIndex;

      const polygon = new Polygon(Calcs.boundingBoxPixelsToRelative(<number[]>opt.target.attrs.points, this.stagePageSizes[pageIndex]));

      this.activeSelectionArea = {
        boundingBox: polygon.boundingBox,
        centroid: polygon.centroid,
        pageNumber: pageNumber,
        pageIndex: pageIndex,
        polygonsInside: Calcs.getPolygonsFromCanvasArea(
          <number[]>opt.target.attrs.points,
          pageIndex,
          this.states.splitWords ? this._wordsGroup : this._linesGroup
        ),
        fixedTooltipPosition: true,
        tooltipPosition: new Point2D(
          opt.evt.offsetX - opt.currentTarget.getStage().content.offsetLeft,
          opt.evt.offsetY - opt.currentTarget.getStage().content.offsetTop
        ),
      };

      if (this.smartCanvasOptions.showGroupKeys) {
        const processed = this.selectedItems.flatMap((x) => x.processedValues)
          ? this.selectedGroupConfiguration?.keys.filter((x) => x.type & this.selectedItems.flatMap((x) => x.processedValues)[0]?.valueType)
          : [];

        const textKeys = this.selectedGroupConfiguration?.keys.filter((x) => x.type === WordDataTypeEnum.text);

        if (this.smartCanvasOptions.expressClassification && (processed?.length === 1 || textKeys.length === 1)) {
          this._eventEmitterService.get('hide-group-key-selection').emit();
          this._eventEmitterService
            .get<ISmartCanvasClickedItemEvent>('clicked-value')
            .emit({ groupKey: processed[0] || textKeys[0], selectedArea: this.activeSelectionArea });
        } else {
          this._eventEmitterService.get<ISmartCanvasSelectionArea>('show-group-key-selection').emit(this.activeSelectionArea);
        }

        this._eventEmitterService.get('hide-canvas-tooltip').emit();
      } else {
        this._eventEmitterService.get('hide-group-key-selection').emit();
        this._eventEmitterService
          .get<ISmartCanvasClickedItemEvent>('clicked-value')
          .emit({ groupKey: null, selectedArea: this.activeSelectionArea });
      }
    }
  }

  private _recreateWords(): void {
    Object.values(this._subscriptions).forEach((subscription) => {
      subscription.unsubscribe();
    });

    this._linesGroup.konvaLayer.destroyChildren();
    this._wordsGroup.konvaLayer.destroyChildren();
    this._wordsGroup.polygons = [];
    this._linesGroup.polygons = [];
    this.activeSelectionArea = null;

    // Só é possível adicionar os elementos ao layer quando as informações de tamanho
    /// da imagem (stagePageSizes) gerada pelo canvas foram definidas. E o stagePageSizes
    /// é atualizado de forma assíncrona conforme as imagens de cada página são carregadas.
    if (this._subscriptions[`page-ready`]) {
      this._subscriptions[`page-ready`].unsubscribe();
    }

    this._subscriptions[`page-ready`] = this._$ready.subscribe((isReady) => {
      if (isReady && this.stagePageSizes?.length > 0 && this.selectedFilePagesAndWords?.length > 0) {
        this._subscriptions[`page-ready`]?.unsubscribe();
        this._drawWordsFromFileData();
      }
    });
  }

  ngOnInit(): void {
    this.layer.konvaLayer.add(this._linesGroup.konvaLayer);
    this.layer.konvaLayer.add(this._wordsGroup.konvaLayer);

    this._eventEmitterService.get<boolean>('show-splitted-words').subscribe((show) => {
      this._showSplittedWords(show);
    });

    this.konvaStage.on('click.canvas-words', (opt) => {
      this._onClick(opt);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedFilePagesAndWords) {
      this._recreateWords();
    }

    if (changes.stagePageSizes?.currentValue && this.stagePageSizes.length > 0) {
      this._$ready.next(true);
    }

    if (changes.smartCanvasOptions?.currentValue) {
      this.states.splitWords = this.smartCanvasOptions.splitWords;
      this._showSplittedWords();

      if (changes.smartCanvasOptions.previousValue?.showWordHighlights !== changes.smartCanvasOptions.currentValue?.showWordHighlights) {
        this.layer.polygons.forEach((polygon) => {
          this._setFillAndOpacityToPoly(polygon.poly);
        });
      }
    }

    if (changes.pagesToShow?.currentValue) {
      this._recreateWords();
    }
  }

  ngOnDestroy(): void {
    this.layer.konvaLayer.destroyChildren();

    Object.values(this._subscriptions).forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
